<template>
  <div>
    <ais-instant-search
      class="d-flex"
      :class="{ toggled: sidebarToggled }"
      id="wrapper"
      :search-client="searchClient"
      index-name="prod_IOMPROPERTY"
    >
      <!-- Sidebar -->
      <div class="bg-light border-right" id="sidebar-wrapper">
        <div class="sidebar-heading">
          <font-awesome-icon icon="sliders-h" class="mr-2"></font-awesome-icon>
          Filter Results
        </div>
        <div class="list-group list-group-flush">
          <div class="list-group-item bg-light">
            <ais-clear-refinements>
              <div slot-scope="{ canRefine, refine, createURL }">
                <a
                  class="btn btn-secondary"
                  :href="createURL()"
                  @click.prevent="refine"
                  :class="{ disabled: !canRefine }"
                >
                  <span v-if="canRefine">
                    <font-awesome-icon
                      icon="times"
                      class="mr-2"
                    ></font-awesome-icon>
                    Clear filters
                  </span>
                  <span v-else>
                    <font-awesome-icon
                      icon="circle"
                      class="mr-2"
                    ></font-awesome-icon>
                    No filters set
                  </span>
                </a>
              </div>
            </ais-clear-refinements>
          </div>

          <div class="list-group-item bg-light">
            <label>
              <font-awesome-icon
                icon="bed"
                title="No. bedrooms"
                class="fa-fw"
              />&nbsp; Bedrooms
            </label>
            <ais-refinement-list attribute="numBedrooms">
              <div
                slot-scope="{
                  items,
                  isShowingMore,
                  isFromSearch,
                  canToggleShowMore,
                  refine,
                }"
              >
                <div
                  class="form-check"
                  v-for="item in items.sort((a, b) => {
                    return a.value - b.value;
                  })"
                  :key="item.value"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :value="item.value"
                    :id="`numBedrooms-filter-${item.value}`"
                    :checked="item.isRefined"
                    @change="refine(item.value)"
                  />
                  <label
                    class="form-check-label"
                    :for="`numBedrooms-filter-${item.value}`"
                  >
                    <ais-highlight attribute="item" :hit="item" />
                    {{ item.value | pluralize('en', ['bedroom', 'bedrooms']) }}
                    <span class="ml-2 badge badge-dark">{{
                      item.count.toLocaleString()
                    }}</span>
                  </label>
                </div>
              </div>
            </ais-refinement-list>
          </div>

          <div class="list-group-item bg-light">
            <label>
              <font-awesome-icon
                icon="bath"
                title="No. bathrooms"
                class="fa-fw"
              />&nbsp; Bathrooms
            </label>
            <ais-refinement-list attribute="numBathrooms">
              <div
                slot-scope="{
                  items,
                  isShowingMore,
                  isFromSearch,
                  canToggleShowMore,
                  refine,
                }"
              >
                <div
                  class="form-check"
                  v-for="item in items.sort((a, b) => {
                    return a.value - b.value;
                  })"
                  :key="item.value"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :value="item.value"
                    :id="`numBathrooms-filter-${item.value}`"
                    :checked="item.isRefined"
                    @change="refine(item.value)"
                  />
                  <label
                    class="form-check-label"
                    :for="`numBathrooms-filter-${item.value}`"
                  >
                    <ais-highlight attribute="item" :hit="item" />
                    {{
                      item.value | pluralize('en', ['bathroom', 'bathrooms'])
                    }}
                    <span class="ml-2 badge badge-dark">{{
                      item.count.toLocaleString()
                    }}</span>
                  </label>
                </div>
              </div>
            </ais-refinement-list>
          </div>

          <div class="list-group-item bg-light">
            <label>
              <font-awesome-icon icon="bed" title="No. receptions" />&nbsp;
              Receptions
            </label>
            <ais-refinement-list attribute="numReceptions">
              <div
                slot-scope="{
                  items,
                  isShowingMore,
                  isFromSearch,
                  canToggleShowMore,
                  refine,
                }"
              >
                <div
                  class="form-check"
                  v-for="item in items.sort((a, b) => {
                    return a.value - b.value;
                  })"
                  :key="item.value"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :value="item.value"
                    :id="`numReceptions-filter-${item.value}`"
                    :checked="item.isRefined"
                    @change="refine(item.value)"
                  />
                  <label
                    class="form-check-label"
                    :for="`numReceptions-filter-${item.value}`"
                  >
                    <ais-highlight attribute="item" :hit="item" />
                    {{
                      item.value | pluralize('en', ['reception', 'receptions'])
                    }}
                    <span class="ml-2 badge badge-dark">{{
                      item.count.toLocaleString()
                    }}</span>
                  </label>
                </div>
              </div>
            </ais-refinement-list>
          </div>

          <div class="list-group-item bg-light">
            <label>
              <font-awesome-icon
                icon="home"
                title="Property type"
                class="fa-fw"
              />&nbsp; Property Type
            </label>
            <ais-refinement-list attribute="type">
              <div
                slot-scope="{
                  items,
                  isShowingMore,
                  isFromSearch,
                  canToggleShowMore,
                  refine,
                }"
              >
                <div class="form-check" v-for="item in items" :key="item.value">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :value="item.value"
                    :id="`type-filter-${item.value}`"
                    :checked="item.isRefined"
                    @change="refine(item.value)"
                  />
                  <label
                    class="form-check-label"
                    :for="`type-filter-${item.value}`"
                  >
                    {{ item.value | humanize }}
                    <span class="ml-2 badge badge-dark">{{
                      item.count.toLocaleString()
                    }}</span>
                  </label>
                </div>
              </div>
            </ais-refinement-list>
          </div>

          <div class="list-group-item bg-light">
            <label>
              <font-awesome-icon
                icon="pound-sign"
                title="Sale price"
                class="fa-fw"
              />&nbsp; Last Sold For
            </label>
            <ais-range-input
              attribute="latestTransaction.consideration"
              :min="0"
              :max="999999999"
            >
              <form
                slot-scope="{ currentRefinement, range, canRefine, refine }"
              >
                <div class="row">
                  <label
                    class="col-3 col-form-label"
                    for="lastTransactionConsiderationMin"
                    >Min</label
                  >
                  <div class="col-9">
                    <select
                      class="form-control"
                      name="lastTransactionConsiderationMin"
                      id="lastTransactionConsiderationMin"
                      @change="
                        refine({
                          min: $event.currentTarget.value,
                          max: formatMaxValue(currentRefinement.max, range.max),
                        })
                      "
                    >
                      <option value="">No min</option>
                      <option
                        v-for="val in salePriceOptions"
                        :value="val"
                        :key="`min-sale-${val}`"
                        :selected="currentRefinement.min === val"
                      >
                        {{ val | formatCurrency }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="form-group row mt-1">
                  <label
                    class="col-3 col-form-label"
                    for="lastTransactionConsiderationMax"
                    >Max</label
                  >
                  <div class="col-9">
                    <select
                      class="form-control"
                      name="lastTransactionConsiderationMin"
                      id="lastTransactionConsiderationMax"
                      @change="
                        refine({
                          min: formatMinValue(currentRefinement.min, range.min),
                          max: $event.currentTarget.value,
                        })
                      "
                    >
                      <option value="">No max</option>
                      <option
                        v-for="val in salePriceOptions"
                        :value="val"
                        :key="`max-sale-${val}`"
                        :selected="currentRefinement.max === val"
                      >
                        {{ val | formatCurrency }}
                      </option>
                    </select>
                  </div>
                </div>
              </form>
            </ais-range-input>
          </div>

          <div class="list-group-item bg-light">
            <label>
              <font-awesome-icon
                icon="calendar-check"
                title="Last sold at"
                class="fa-fw"
              />&nbsp; Last Sold At
            </label>

            <ais-range-input
              attribute="latestTransaction.acquisitionDateTimestamp"
              :min="moment('1990-01-01', 'YYYY-MM-DD').unix()"
              :max="moment().unix()"
            >
              <form
                slot-scope="{ currentRefinement, range, canRefine, refine }"
              >
                <div class="row">
                  <label
                    class="col-3 col-form-label"
                    for="lastTransactionConsiderationMin"
                    >From</label
                  >
                  <div class="col-9">
                    <select
                      class="form-control"
                      name="lastTransactionConsiderationMin"
                      id="lastTransactionConsiderationMin"
                      @change="
                        refine({
                          min: $event.currentTarget.value,
                          max: formatMaxValue(currentRefinement.max, range.max),
                        })
                      "
                    >
                      <option value="">No min</option>
                      <option
                        v-for="month in saleMonthOptions"
                        :value="month.start"
                        :key="`min-month-${month.label}`"
                        :selected="currentRefinement.min === month.start"
                      >
                        {{ month.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="form-group row mt-1">
                  <label
                    class="col-3 col-form-label"
                    for="lastTransactionConsiderationMax"
                    >To</label
                  >
                  <div class="col-9">
                    <select
                      class="form-control"
                      name="lastTransactionConsiderationMin"
                      id="lastTransactionConsiderationMax"
                      @change="
                        refine({
                          min: formatMinValue(currentRefinement.min, range.min),
                          max: $event.currentTarget.value,
                        })
                      "
                    >
                      <option value="">No max</option>
                      <option
                        v-for="month in saleMonthOptions"
                        :value="month.end"
                        :key="`max-month-${month.label}`"
                        :selected="currentRefinement.max === month.end"
                      >
                        {{ month.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </form>
            </ais-range-input>
          </div>
        </div>
      </div>
      <!-- /#sidebar-wrapper -->

      <!-- Page Content -->
      <div id="page-content-wrapper">
        <nav
          class="navbar navbar-expand-lg bg-primary text-light border-bottom"
        >
          <span class="navbar-nav mr-auto">
            <button
              class="d-inline d-md-none btn btn-secondary mr-2"
              id="menu-toggle"
              @click="sidebarToggled = !sidebarToggled"
            >
              <font-awesome-icon
                icon="sliders-h"
                title="Toggle filters"
                class="mr-2"
              />
              <!-- <span v-if="sidebarToggled">Show</span>
              <span v-if="!sidebarToggled">Hide</span> Filters -->
              <span>Toggle Filters</span>
            </button>
          </span>

          <strong class="my-2 my-lg-0">
            <span>SoldFor.im</span>
            <span class="d-none d-md-inline">
              &mdash; The Isle of Man Property Sales Database</span
            >
          </strong>
        </nav>

        <div class="container-fluid">
          <div class="search-panel__results">
            <div class="row mt-3">
              <div class="col-12 col-sm-8 col-md-4">
                <ais-search-box>
                  <div
                    slot-scope="{ currentRefinement, isSearchStalled, refine }"
                    class="input-group"
                  >
                    <div class="input-group-prepend">
                      <span class="input-group-text" id="basic-addon1">
                        <font-awesome-icon icon="search"></font-awesome-icon>
                      </span>
                    </div>
                    <input
                      type="search"
                      class="form-control"
                      v-model="currentRefinement"
                      placeholder="Try a house or street name e.g. Bishopscourt"
                      @input="refine($event.currentTarget.value)"
                    />
                    <div class="input-group-append">
                      <span :hidden="!isSearchStalled" class="input-group-text"
                        >Loading&hellip;</span
                      >
                    </div>
                  </div>
                </ais-search-box>
              </div>
              <div class="d-none d-sm-block col-12 col-sm-4">
                <ais-hits-per-page
                  :items="[
                    { label: '10 results per page', value: 10 },
                    { label: '25 results per page', value: 25 },
                    { label: '50 results per page', value: 50, default: true },
                    { label: '100 results per page', value: 100 },
                  ]"
                >
                  <select
                    slot-scope="{ items, refine, createURL }"
                    class="form-control"
                    @change="refine($event.target.value)"
                  >
                    <option
                      v-for="item in items"
                      :key="item.value"
                      :value="item.value"
                      :selected="item.isRefined"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                </ais-hits-per-page>
              </div>
              <div class="d-none d-md-block col-12 col-md-4">
                <ais-stats class="h-100">
                  <span
                    slot-scope="{ hitsPerPage, nbPages, nbHits }"
                    class="text-muted align-self-center"
                  >
                    Showing
                    {{
                      (nbHits < hitsPerPage ? nbHits : hitsPerPage)
                        | formatNumber
                    }}
                    of {{ nbHits | formatNumber }} properties.
                    <!-- NOTE: set this every time new data added to the database -->
                    <!-- TODO: automate this using an API lookup -->
                    <span class="d-none d-md-inline">Updated 2022-09-29.</span>
                  </span>
                </ais-stats>
              </div>
            </div>

            <div id="map-container" class="row mt-3">
              <div class="col-12"><app-map /></div>
            </div>

            <ais-hits>
              <div slot-scope="{ items }" class="row mt-3">
                <div
                  v-for="item in items"
                  :key="item.id"
                  :id="`result-${item.id}`"
                  class="col-12 col-lg-6 col-xl-4 mb-3"
                >
                  <div class="card h-100">
                    <img
                      class="card-img-top"
                      :src="
                        item.imageUrl1 || '/assets/img/default-property.png'
                      "
                    />

                    <div class="card-body">
                      <h5 class="card-title">
                        <span v-if="item.houseName"
                          >{{ item.houseName }},&nbsp;</span
                        >
                        <span v-if="item.houseNumber"
                          >{{ item.houseNumber }}&nbsp;</span
                        >
                        <span v-if="item.streetName"
                          >{{ item.streetName }},&nbsp;</span
                        >
                        <span v-if="item.postcode">{{ item.postcode }}</span>
                      </h5>
                    </div>

                    <ul class="list-group list-group-flush">
                      <li class="list-group-item">
                        <div class="row">
                          <div class="col-4 col-md-2">
                            <font-awesome-icon
                              icon="bed"
                              title="No. bedrooms"
                              class="mr-1 md-mr-3 text-muted"
                            />
                            <span v-if="item.numBedrooms">
                              {{ item.numBedrooms }}
                            </span>
                            <font-awesome-icon
                              v-else
                              icon="question"
                              title="Data unavailable"
                              class="text-v-muted"
                            ></font-awesome-icon>
                          </div>

                          <div class="col-4 col-md-2">
                            <font-awesome-icon
                              icon="bath"
                              title="No. bathrooms"
                              class="mr-1 md-mr-3 text-muted"
                            />
                            <span v-if="item.numBathrooms">
                              {{ item.numBathrooms }}
                            </span>
                            <font-awesome-icon
                              v-else
                              icon="question"
                              title="Data unavailable"
                              class="text-v-muted"
                            ></font-awesome-icon>
                          </div>

                          <div class="col-4 col-md-2">
                            <font-awesome-icon
                              icon="couch"
                              title="No. reception rooms"
                              class="mr-1 md-mr-3 text-muted"
                            />
                            <span v-if="item.numReceptions">
                              {{ item.numReceptions }}
                            </span>
                            <font-awesome-icon
                              v-else
                              icon="question"
                              title="Data unavailable"
                              class="text-v-muted"
                            ></font-awesome-icon>
                          </div>

                          <div class="col-12 col-md-6">
                            <font-awesome-icon
                              icon="home"
                              title="Property type"
                              class="mr-1 md-mr-3 text-muted"
                            />
                            <span v-if="item.type">
                              {{ item.type | humanize }}
                            </span>
                            <font-awesome-icon
                              v-else
                              icon="question"
                              title="Data unavailable"
                              class="text-v-muted"
                            ></font-awesome-icon>
                          </div>
                        </div>
                      </li>
                      <li class="list-group-item">
                        <div class="row">
                          <div class="col-3"><strong>Sold for:</strong></div>
                          <div class="col-4">
                            {{
                              item.latestTransaction.consideration
                                | formatCurrency
                            }}
                          </div>
                          <div class="col-4">
                            <span class="text-muted">
                              {{
                                item.latestTransaction.acquisitionDate
                                  | moment('MMM Do YYYY')
                              }}
                            </span>
                          </div>
                        </div>
                      </li>
                      <!-- <li class="list-group-item">
                        <div class="row">
                          <div class="col-3"><strong>Previous:</strong></div>
                          <div class="col-4">
                            <span v-if="item.transactions[1]">
                              {{
                                item.transactions[1].consideration
                                  | formatCurrency
                              }}
                            </span>
                            <span v-else> Data unavailable </span>
                          </div>
                          <div class="col-4">
                            <span
                              class="text-muted"
                              v-if="item.transactions[1]"
                            >
                              {{
                                item.transactions[1].acquisitionDate
                                  | moment('MMM Do YYYY')
                              }}
                            </span>
                          </div>
                        </div>
                      </li> -->
                    </ul>

                    <div class="card-footer text-right">
                      <a
                        class="card-link"
                        href="#"
                        v-on:click="openPropertyTransactionModalId = item.id"
                      >
                        <font-awesome-icon
                          icon="calendar-check"
                          class="mr-1 text-muted"
                        />
                        Sale history
                        <span class="ml-2 badge badge-dark">{{
                          item.transactions.length
                        }}</span></a
                      >&nbsp;
                      <a
                        v-if="item.externalUrl"
                        target="_blank"
                        title="Agent listing"
                        :href="item.externalUrl"
                        class="card-link"
                      >
                        <font-awesome-icon
                          icon="link"
                          class="mr-1 text-muted"
                        />
                        Listing
                      </a>
                      &nbsp;
                      <a
                        v-if="item.postcode"
                        target="_blank"
                        title="Search planning applications"
                        :href="
                          `https://services.gov.im/planningapplication/services/planning/addresssearch.iom?InitialSearch=${item.postcode.replace(
                            ' ',
                            '+'
                          )}`
                        "
                        class="card-link"
                      >
                        <font-awesome-icon icon="map" class="mr-1 text-muted" />
                        Planning
                      </a>
                    </div>

                    <modal
                      :show="openPropertyTransactionModalId === item.id"
                      :showClose="true"
                      :showHeader="true"
                      v-on:close="openPropertyTransactionModalId = null"
                    >
                      <h5 slot="header" class="modal-title">
                        Transaction history
                      </h5>

                      <div class="list-group list-group-flush">
                        <div
                          class="list-group-item"
                          v-for="(transaction, index) in item.transactions"
                          :key="transaction.id"
                        >
                          <div class="row">
                            <div class="col-2">
                              <strong
                                >#
                                {{ item.transactions.length - index }}</strong
                              >
                            </div>
                            <div class="col-5">
                              {{ transaction.consideration | formatCurrency }}
                            </div>
                            <div class="col-5">
                              <span class="text-muted">
                                {{
                                  transaction.acquisitionDate
                                    | moment('MMM Do YYYY')
                                }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div slot="footer" class="text-right">
                        <button
                          class="btn btn-default"
                          @click="openPropertyTransactionModalId = null"
                        >
                          <font-awesome-icon
                            icon="times"
                            class="mr-2"
                          ></font-awesome-icon>
                          Close
                        </button>
                      </div>
                    </modal>
                  </div>
                </div>
              </div>
            </ais-hits>
            <div class="pagination">
              <nav aria-label="Results pagination" class="w-100">
                <ais-pagination class="d-flex">
                  <ul
                    slot-scope="{
                      currentRefinement,
                      nbPages,
                      pages,
                      isFirstPage,
                      isLastPage,
                      refine,
                      createURL,
                    }"
                    class="pagination mx-auto"
                  >
                    <!-- First page -->
                    <li class="page-item" :class="{ disabled: isFirstPage }">
                      <a
                        class="page-link"
                        :href="createURL(0)"
                        @click.prevent="refine(0)"
                        tabindex="-1"
                        title="First page"
                      >
                        <font-awesome-icon icon="backward"></font-awesome-icon>
                      </a>
                    </li>

                    <!-- Previous page -->
                    <li class="page-item" :class="{ disabled: isFirstPage }">
                      <a
                        class="page-link"
                        :href="createURL(currentRefinement - 1)"
                        @click.prevent="refine(currentRefinement - 1)"
                        tabindex="-1"
                        title="Previous page"
                      >
                        <font-awesome-icon
                          icon="caret-left"
                        ></font-awesome-icon>
                      </a>
                    </li>

                    <!-- Specific pages -->
                    <li
                      v-for="page in pages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === currentRefinement }"
                    >
                      <a
                        class="page-link"
                        :href="createURL(page)"
                        @click.prevent="refine(page)"
                      >
                        {{ page + 1 }}
                      </a>
                    </li>

                    <!-- Next page -->
                    <li class="page-item" :class="{ disabled: isLastPage }">
                      <a
                        class="page-link"
                        :href="createURL(currentRefinement + 1)"
                        @click.prevent="refine(currentRefinement + 1)"
                        title="Next page"
                      >
                        <font-awesome-icon
                          icon="caret-right"
                        ></font-awesome-icon>
                      </a>
                    </li>

                    <!-- Last page -->
                    <li class="page-item" :class="{ disabled: isLastPage }">
                      <a
                        class="page-link"
                        :href="createURL(nbPages)"
                        @click.prevent="refine(nbPages)"
                        title="Last page"
                      >
                        <font-awesome-icon icon="forward"></font-awesome-icon>
                      </a>
                    </li>
                  </ul>
                </ais-pagination>
              </nav>
            </div>
          </div>
        </div>

        <footer class="footer p-4 text-center">
          <span class="text-muted"
            >Site by <a href="https://melior.im">Melior</a> | &copy;
            {{ new Date().getFullYear() }} Melior Enterprises Ltd | Contact
            <a href="mailto:hi@soldfor.im">hi@soldfor.im</a><br />
            Isle of Man Land Transactions Data from the Isle of Man Land
            Registry used under
            <a
              href="https://www.gov.im/about-this-site/open-government-licence/"
              >Open Government Licence</a
            >
          </span>
        </footer>
      </div>
      <!-- /#page-content-wrapper -->
    </ais-instant-search>
  </div>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import humanizeString from 'humanize-string';
import moment from 'moment';
import AppMap from './components/Map';
import Modal from './components/Modal';

function generateDateOptions(earliestDate) {
  let testDate = moment();
  const months = [testDate.format('YYYY-MM-DD')];

  while (moment(months[months.length - 1]).isAfter(moment(earliestDate))) {
    testDate.subtract(1, 'month');
    months.push(testDate.startOf('month').format('YYYY-MM-DD'));
  }

  const filters = months.map((item) => {
    return {
      label: moment(item).format('MMM YYYY'),
      start: moment(item)
        .startOf('month')
        .unix(),
      end: moment(item)
        .endOf('month')
        .unix(),
    };
  });

  return filters;
}

export default {
  components: {
    AppMap,
    Modal,
  },
  data() {
    return {
      searchClient: algoliasearch(
        'H4U117TF8Y',
        'b51c3fda7a1f9f3ca87a8421a6273b03'
      ),
      sidebarToggled: false,
      openPropertyTransactionModalId: null,
      salePriceOptions: [
        10000,
        20000,
        30000,
        40000,
        50000,
        60000,
        70000,
        80000,
        90000,
        100000,
        110000,
        120000,
        130000,
        140000,
        150000,
        160000,
        170000,
        180000,
        190000,
        200000,
        210000,
        220000,
        230000,
        240000,
        250000,
        275000,
        300000,
        325000,
        350000,
        375000,
        400000,
        425000,
        450000,
        475000,
        500000,
        550000,
        600000,
        650000,
        700000,
        750000,
        800000,
        850000,
        900000,
        950000,
        1000000,
        1100000,
        1200000,
        1300000,
        1400000,
        1500000,
        1600000,
        1700000,
        1800000,
        1900000,
        2000000,
        2100000,
        2200000,
        2300000,
        2400000,
        2500000,
        2775000,
        3000000,
        3250000,
        3500000,
        3750000,
        4000000,
        4250000,
        4500000,
        4750000,
        5000000,
        5500000,
        6000000,
        6500000,
        7000000,
        8000000,
        8500000,
        9000000,
        9500000,
        10000000,
        12500000,
        15000000,
      ],
      saleMonthOptions: generateDateOptions('1900-01-01'),
      moment,
    };
  },
  methods: {
    formatMinValue(minValue, minRange) {
      return minValue !== null && minValue !== minRange ? minValue : '';
    },
    formatMaxValue(maxValue, maxRange) {
      return maxValue !== null && maxValue !== maxRange ? maxValue : '';
    },
  },
  filters: {
    humanize: function(str) {
      if (!str) return '';
      return humanizeString(str);
    },
    formatCurrency: function(num) {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(num);
    },
    formatNumber: function(num) {
      return new Intl.NumberFormat('en-GB', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    },
  },
};
</script>

<style>
.ais-Stats {
  display: flex;
}

.footer {
  /* position: absolute; */
  /* bottom: 0;
  width: 100%;
  height: 60px;
  line-height: 60px; */
  background-color: #f5f5f5;
}

#map-container {
  height: 450px;
}

.text-v-muted {
  color: #d6dbdf;
}

/* body,
h1 {
  margin: 0;
  padding: 0;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
}

.header {
  display: flex;
  align-items: center;
  min-height: 50px;
  padding: 0.5rem 1rem;
  background-image: linear-gradient(to right, #4dba87, #2f9088);
  color: #fff;
  margin-bottom: 1rem;
}

.header a {
  color: #fff;
  text-decoration: none;
}

.header-title {
  font-size: 1.2rem;
  font-weight: normal;
}

.header-subtitle {
  font-size: 1.2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.search-panel {
  display: flex;
}

.search-panel__filters {
  flex: 1;
  margin-right: 1em;
}

.search-panel__results {
  flex: 3;
}

.filter-container {
  margin-bottom: 1.5em;
}

.filter-label {
  margin-bottom: 0.5em;
}

.search-stats-container {
  margin-bottom: 1em;
}

.searchbox {
  margin-bottom: 2rem;
}

.pagination {
  margin: 2rem auto;
  text-align: center;
}

.ais-GeoSearch {
  margin-bottom: 1rem;
}

.ais-Hits-item,
.ais-InfiniteHits-item,
.ais-InfiniteResults-item,
.ais-Results-item {
  margin-top: 1rem;
  margin-left: 1rem;
  padding: 1rem;
  width: calc(50% - 1rem);
  border: 1px solid #c4c8d8;
  -webkit-box-shadow: 0 2px 5px 0 #e3e5ec;
  box-shadow: 0 2px 5px 0 #e3e5ec;
}

.property-attributes {
  margin-top: 0.5em;
  display: flex;
}

.property-rooms {
  flex: 1;
}

.property-type {
  flex: 1;
}

.img-responsive {
  max-width: 100%;
  display: block;
  height: auto;
}

.small {
  font-size: 0.8em;
}

.text-muted {
  color: gray;
} */
</style>
